/* eslint-disable react-hooks/exhaustive-deps */
"use client"

import { useState, useEffect } from "react"
import { Box, Grid, Paper, CircularProgress, Typography } from "@mui/material"
import { VerticalBarChart } from "../../components/vertical-bar-chart"
import { HorizontalBarChart } from "../../components/horizontal-bar-chart"
import { useSearchParams } from 'react-router-dom'
import { PieChart } from "./pie-chart"
import { FiltersSection } from "../../components/filters-section"
import { formatDateString } from "../../utils/date"

const LEFT_WIDTH = 35

interface ProjectDimension {
  Code: string
  Name: string
  CountTotal: number
}

interface Project {
  ProjectId: string
  ProjectName: string
  ProjectCode: string
  Dimensions: ProjectDimension[]
}

interface MarkByDate {
  Date: string
  CountTotal: number
}

interface CountByMarkType {
  CountVL: number
  CountCard: number
  CountFinger: number
}

interface CountByActive {
  CountTotal: number
  CountActive: number
  CountInactive: number
}

const getLastMonthDates = () => {
  const endDate = new Date()
  const startDate = new Date()
  startDate.setMonth(endDate.getMonth() - 1)
  
  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0]
  }
  
  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate)
  }
}

const fetchWithToken = async (url: string, token: string, moduleId: string) => {
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Token': token.replaceAll(' ', '+'),
      'ModuleId': moduleId,
      'Content-Type': 'application/json'
    }
  })
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  
  return await response.json()
}

export function AttendanceDashboard() {
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  const [projectsData, setProjectsData] = useState<Project[]>([])
  const [activeStatusData, setActiveStatusData] = useState<CountByActive | null>(null)
  const [markTypeData, setMarkTypeData] = useState<CountByMarkType | null>(null)
  const [marksByDate, setMarksByDate] = useState<MarkByDate[]>([])
  
  const [selectedProject, setSelectedProject] = useState(0)
  const [selectedStatus, setSelectedStatus] = useState(0)
  const [selectedProjectCode, setSelectedProjectCode] = useState<string>('')
  
  const defaultDates = getLastMonthDates()
  
  const [filterValues, setFilterValues] = useState<Record<string, string>>({
    mode: 'cantidad',
    period: 'week',
    chartType: 'bars',
    startDate: defaultDates.startDate,
    endDate: defaultDates.endDate
  })
  
  const token = searchParams.get('token') || ''
  const moduleId = searchParams.get('moduleId') || ''
  const baseUrl = `${process.env.REACT_APP_S10_BASE_API}/Clock`

  useEffect(() => {
    if (!selectedProjectCode || !filterValues.startDate || !filterValues.endDate) {
      return;
    }
    fetchMarksByDate(selectedProjectCode, filterValues.startDate, filterValues.endDate)
  }, [selectedProjectCode, filterValues.startDate, filterValues.endDate])

  const fetchProjects = async () => {
    try {
      const url = `${baseUrl}/Api/DashboardApi/GetCountEmployeesByProject`
      const data = await fetchWithToken(url, token, moduleId)
      if (data.Type === 0 && data.Value && data.Value.length > 0) {
        const sortedProjects = [...data.Value].sort((a, b) => {
          const totalA = a.Dimensions.reduce((sum: any, dim: any) => sum + dim.CountTotal, 0)
          const totalB = b.Dimensions.reduce((sum: any, dim: any) => sum + dim.CountTotal, 0)
          return totalB - totalA
        })
        setProjectsData(sortedProjects)
        const firstProjectCode = sortedProjects[0].ProjectCode
        setSelectedProjectCode(firstProjectCode)
        return firstProjectCode
      }
      return null
    } catch (err) {
      console.error('Error fetching projects:', err)
      throw err
    }
  }

  const fetchActiveStatus = async (projectCode: string) => {
    try {
      const url = `${baseUrl}/Api/DashboardApi/GetCountEmployeesByActive?projectCode=${projectCode}`
      const data = await fetchWithToken(url, token, moduleId)
      if (data.Type === 0 && data.Value) {
        setActiveStatusData(data.Value)
      }
    } catch (err) {
      console.error('Error fetching active status:', err)
      throw err
    }
  }

  const fetchMarkType = async (projectCode: string) => {
    try {
      const url = `${baseUrl}/Api/DashboardApi/GetCountEmployeesByMarkType?projectCode=${projectCode}`
      const data = await fetchWithToken(url, token, moduleId)
      if (data.Type === 0 && data.Value) {
        setMarkTypeData(data.Value)
      }
    } catch (err) {
      console.error('Error fetching mark type:', err)
      throw err
    }
  }

  const fetchMarksByDate = async (projectCode: string, startDate: string, endDate: string) => {
    try {
      const url = `${baseUrl}/Api/DashboardApi/GetCountMarksByDate?fechaInicio=${startDate}&fechaFin=${endDate}&projectCode=${projectCode}`
      const data = await fetchWithToken(url, token, moduleId)
      if (data.Type === 0 && data.Value) {
        const sortedMarks = [...data.Value].sort((a, b) => 
          new Date(b.Date).getTime() + new Date(a.Date).getTime()
        )
        setMarksByDate(sortedMarks)
      }
    } catch (err) {
      console.error('Error fetching marks by date:', err)
      throw err
    }
  }

  const loadRemainingData = async (projectCode: string) => {
    try {
      await Promise.all([
        fetchActiveStatus(projectCode),
        fetchMarkType(projectCode),
      ])
    } catch (err) {
      console.error('Error loading remaining data:', err)
      throw err
    }
  }

  const loadDashboardData = async () => {
    setLoading(true)
    setError(null)
    try {
      const firstProjectCode = await fetchProjects()
      if (firstProjectCode) {
        await loadRemainingData(firstProjectCode)
      }
    } catch (err) {
      setError('Error al cargar los datos del dashboard')
      console.error('Error loading dashboard data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleProjectSelect = async (index: number) => {
    setSelectedProject(index)
    setSelectedStatus(0)
    
    const projectCode = projectsData[index]?.ProjectCode
    if (projectCode) {
      setSelectedProjectCode(projectCode)
      setLoading(true)
      try {
        await loadRemainingData(projectCode)
      } catch (err) {
        setError('Error al cargar los datos del proyecto seleccionado')
      } finally {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    loadDashboardData()
  }, [token, moduleId])

  const getTransformedProjectsData = () => {
    if (!projectsData.length) return []
    
    return projectsData.map(project => ({
      id: project.ProjectId,
      label: project.ProjectName.length > 30 ? project.ProjectName.substring(0, 27) + '...' : project.ProjectName,
      value: project.Dimensions.reduce((sum, dim) => sum + dim.CountTotal, 0),
      color: "rgba(34, 197, 94, 0.7)"
    }))
  }

  const getTransformedStatusData = () => {
    if (!activeStatusData) return []
    
    return [
      { 
        id: "active", 
        label: "Activos", 
        value: activeStatusData.CountActive, 
        color: "rgba(168, 85, 247, 0.8)" 
      },
      { 
        id: "inactive", 
        label: "No Activos", 
        value: activeStatusData.CountInactive, 
        color: "rgba(251, 146, 60, 0.8)" 
      }
    ]
  }

  const getTransformedMarkTypeData = () => {
    if (!markTypeData) return []
    
    const data = []
    if (markTypeData.CountVL > 0) {
      data.push({ 
        id: "rosto-vl", 
        label: "Rostro VL", 
        value: markTypeData.CountVL, 
        color: "rgba(59, 130, 246, 0.7)" 
      })
    }
    if (markTypeData.CountCard > 0) {
      data.push({ 
        id: "tarjeta", 
        label: "Tarjeta", 
        value: markTypeData.CountCard, 
        color: "rgba(59, 130, 246, 0.7)" 
      })
    }
    if (markTypeData.CountFinger > 0) {
      data.push({ 
        id: "huellas", 
        label: "Huellas", 
        value: markTypeData.CountFinger, 
        color: "rgba(59, 130, 246, 0.7)" 
      })
    }
    
    return data.sort((a, b) => b.value - a.value)
  }

  const getTransformedMarksByDate = () => {
    if (!marksByDate.length) return []
    
    return marksByDate.map(mark => ({
      id: mark.Date,
      // label: new Date(mark.Date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }),
      label: formatDateString(mark.Date),
      value: mark.CountTotal,
      color: "rgba(59, 130, 246, 0.7)"
    }))
  }

  const handleFilterChange = (filterId: string, value: string) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterId]: value,
    }))
  }

  if (loading && !projectsData.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  const transformedProjects = getTransformedProjectsData()
  const transformedStatus = getTransformedStatusData()
  const transformedMarkType = getTransformedMarkTypeData()
  const transformedMarks = getTransformedMarksByDate()

  return (
    <Grid container sx={{ width: "100%", height: "100%", display: "flex", minHeight: '90vh'}}>
      <Grid container spacing={2} sx={{ minHeight: '45%', maxHeight: '45vh' }}>
        <Grid item sx={{ width: `${LEFT_WIDTH}%` }}>
          <Paper sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
            <HorizontalBarChart
              title="Personas por proyecto"
              data={transformedProjects}
              selectedIndex={selectedProject}
              onSelect={handleProjectSelect}
              barThickness={20}
              itemsPerPage={8}
              enablePagination={true}
            />
          </Paper>
        </Grid>

        <Grid item sx={{ width: `${(100 - LEFT_WIDTH) / 2}%` }}>
          <Paper sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
            <PieChart
              title="Personal Activo / No Activo"
              data={transformedStatus}
              selectedIndex={selectedStatus}
              onSelect={(index) => setSelectedStatus(index)}
            />
          </Paper>
        </Grid>

        <Grid item sx={{ width: `${(100 - LEFT_WIDTH) / 2}%` }}>
          <Paper sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
            <HorizontalBarChart
              title="Tipo de Marcación"
              data={transformedMarkType}
              selectedIndex={-1}
              onSelect={() => {}}
              barThickness={30}
              enablePagination={true}
            />
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ minHeight: '45%', maxHeight: '45vh' }}>
        <Box
          sx={{
            display: "flex",
            gap: 3,
            alignItems: "center",
            flexWrap: "wrap",
            p: 2,
            backgroundColor: "rgba(0, 0, 0, 0.02)",
            borderRadius: 1,
          }}
        >
          <FiltersSection
            filters={[
              {
                label: "Fecha Inicio",
                value: "startDate",
                type: "date",
                minDate: new Date(2024, 0, 1),
                maxDate: new Date()
              },
              {
                label: "Fecha Fin",
                value: "endDate",
                type: "date",
                minDate: new Date(2024, 0, 1),
                maxDate: new Date()
              },
              // {
              //   label: "Modo",
              //   value: "mode",
              //   options: [
              //     { label: "Cantidad", value: "cantidad" },
              //     { label: "Porcentaje", value: "porcentaje" },
              //   ],
              // },
              // {
              //   label: "Periodo",
              //   value: "period",
              //   options: [
              //     { label: "Diario", value: "day" },
              //     { label: "Semanal", value: "week" },
              //     { label: "Mensual", value: "month" },
              //     { label: "Anual", value: "year" },
              //   ],
              // },
              // {
              //   label: "Gráfico",
              //   value: "chartType",
              //   options: [
              //     { label: "Barras", value: "bars" },
              //     { label: "Líneas", value: "lines" },
              //   ],
              // },
            ]} 
            onFilterChange={handleFilterChange} 
            values={filterValues} 
          />
        </Box>

        <Paper sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <VerticalBarChart
            title="Marcaciones por fecha"
            data={transformedMarks}
            chartType={filterValues.chartType as any}
            mode={filterValues.mode as any}
            enablePagination={true}
            itemsPerPage={15}
          />
        </Paper>
      </Grid>
    </Grid>
  )
}